/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    inject,
    Injector,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    output,
    SimpleChanges,
    SkipSelf,
    ViewChild
} from '@angular/core';
import { ResizeObserver } from '@juggle/resize-observer';
import { DigitizePointService } from '@map/command/digitize-point/digitize-point.service';
import { LaunchStreetView } from '@map/state/active-map.actions';
import { LaunchStreetViewPayload } from '@map/state/active-map.model';
import { FlexDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Actions, Store } from '@ngxs/store';
import { ReportControlsComponent } from '@reports/interface/reportControls/report-controls.component';
import { ReportContentContainerComponent } from '@reports/reportContent/report-content-container.component';
import { isolatedReportFactory, TypedReportDirective } from '@reports/reportTypes/typed-report.directive';
import { IsolatedReportService } from '@reports/services/isolatedReport.service';
import { ReportService } from '@reports/services/report.service';
import { ClearParcelDisplayOptions, SetParcelImageSize } from '@reports/state/report.actions';
import { ParcelDisplayAddress, ParcelDisplayOptions, ReportLaunchOptions, ReportSubtypeValue } from '@reports/state/report.model';
import { ReportState } from '@reports/state/report.state';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';
import { isEqual, throttle } from 'lodash-es';
import { filter, Observable, take } from 'rxjs';
import { defaultIfEmpty, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'dash-gis-feature-report',
    templateUrl: './feature-report.component.html',
    styleUrls: [ './feature-report.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: IsolatedReportService,
            useFactory: isolatedReportFactory,
            deps: [ Injector, [ new Optional(), new SkipSelf(), IsolatedReportService ] ]
        }
    ],
    imports: [
        FlexDirective,
        LayoutDirective,
        ReportControlsComponent,
        ReportContentContainerComponent,
        AsyncPipe
    ]
})
export class FeatureReportComponent
    extends TypedReportDirective
    implements OnInit, OnChanges, OnDestroy, DoCheck, AfterContentChecked, AfterViewInit {

    overrideSubtype = output<ReportSubtypeValue>();
    widthUpdate = output<number>();

    parcelDisplayAddress$: Observable<ParcelDisplayAddress>;
    streetViewStaticUrl$: Observable<string>;

    digitizeKey$: Observable<any>;

    protected parcelImageLoaded = false;

    private parcelImageUrl$: Observable<(id: string, reportName: string) => string> = inject(Store).select(
        ReportState.parcelImageUrl
    );

    private parcelDisplayAddressFunc$: Observable<(id: string, reportName: string) => ParcelDisplayAddress> = inject(
        Store
    ).select(ReportState.parcelDisplayAddress);

    @ViewChild('parcelImageContainer', { read: ElementRef })
    private parcelImageContainer: ElementRef;

    private reportDataInternal: any[];
    private subTypeInternal: ReportSubtypeValue;
    private resizeObserver: ResizeObserver;

    constructor(
        logger: LoggerService,
        reportService: ReportService,
        isolatedReport: IsolatedReportService,
        cdr: ChangeDetectorRef,
        actions$: Actions,
        store: Store,
        el: ElementRef,
        private digitizeService: DigitizePointService
    ) {
        super(logger, reportService, isolatedReport, cdr, actions$, store, el);
        this.isolatedReport.hasDataSource$.pipe(
            filter(Boolean),
            take(1)
        ).subscribe(() => {
            this.digitizeKey$ = this.isolatedReport.digitizeKey$;
        });
    }

    ngOnInit() {
        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.launchOptions) {
            const current: ReportLaunchOptions = changes.launchOptions.currentValue;
            const previous: ReportLaunchOptions = changes.launchOptions.previousValue;

            if (current && !isEqual(current, previous)) {
                this.reportChange();

                this.parcelDisplayAddress$ = this.getParcelDisplayAddress$();
                this.streetViewStaticUrl$ = this.getStreetViewStaticUrl$();
            }
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    ngDoCheck(): void {
        super.ngDoCheck();
    }

    ngAfterContentChecked() {
        super.ngAfterContentChecked();

        if (this.parcelImageContainer && !this.resizeObserver) {
            this.initializeResizeObserver();
        }
    }

    ngAfterViewInit(): void {
        this.featureReportKeys$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
            .subscribe((keys: any[]) => {
                if (keys && keys.length > 0) {
                    this.reportChange();
                }
            });
    }

    onImageLoaded() {
        this.parcelImageLoaded = true;
    }

    setParcelReportImageSize(resizeEntries: ResizeObserverEntry[]) {
        resizeEntries.forEach((entry) => {
            if (entry.contentRect?.width && entry.contentRect?.height) {
                this.store.dispatch(
                    new SetParcelImageSize({
                        id: this.id,
                        reportName: this.definition.name,
                        width: Math.floor(entry.contentRect.width),
                        height: Math.floor(entry.contentRect.height)
                    })
                );
            }
        });
    }

    launchStreetView() {
        const panoIdFn = this.store.selectSnapshot<(id: string, reportName: string) => ParcelDisplayOptions | null>(
            ReportState.parcelDisplayOptions
        );
        const opts = panoIdFn(this.id, this.definition.name);
        if (opts?.pano) {
            const payload: LaunchStreetViewPayload = { pano: opts.pano };
            if (opts?.heading) {
                payload.heading = opts.heading;
            }
            this.store.dispatch(new LaunchStreetView(payload));
        }
    }

    setRecordCount(count: number) {
        super.setRecordCount(count);
        this.updateSubType();
    }

    private getStreetViewStaticUrl$(): Observable<string> {
        return this.parcelImageUrl$.pipe(
            map((fn) => fn(this.isolatedReport.id, this.definition.name)),
            distinctUntilChanged(),
            defaultIfEmpty(''),
            tap((url) => {
                if (!url) {
                    this.parcelImageLoaded = false;
                }
            })
        );
    }

    private getParcelDisplayAddress$(): Observable<ParcelDisplayAddress> {
        return this.parcelDisplayAddressFunc$.pipe(
            map((fn) => fn(this.isolatedReport.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );
    }

    private initializeResizeObserver() {
        this.resizeObserver = new ResizeObserver(
            throttle((e) => this.setParcelReportImageSize(e), 500, {
                leading: false,
                trailing: true
            })
        );
        this.resizeObserver.observe(this.parcelImageContainer.nativeElement);
    }

    private reportChange() {
        this.updateSubType();
        this.parcelImageLoaded = false;
        this.store.dispatch(
            new ClearParcelDisplayOptions({
                id: this.id,
                reportName: this.definition.name
            })
        );
        let width = DashGisUtil.pixelValue(this.definition.displayWidth);
        if (!width) {
            switch (this.subTypeInternal) {
                case 'detail':
                    width = 450;
                    break;
                default:
                    width = 900;
                    break;
            }
        }
        this.widthUpdate.emit(width);
    }

    private updateSubType() {
        let subtype: ReportSubtypeValue;
        switch (true) {
            case this.recordCountInternal < 0:
                subtype = 'pending';
                break;
            case this.recordCountInternal === 0:
                subtype = 'empty';
                break;
            case this.definition.subType === 'label':
                subtype = 'label';
                break;
            case this.definition.subType === 'chart':
                subtype = 'chart';
                break;
            case this.recordCountInternal === 1:
                subtype = 'detail';
                break;
            case this.recordCountInternal > 1:
                subtype = 'tabular';
                break;
            default:
                subtype = this.definition.subType;
        }
        this.subTypeInternal = subtype;
        this.overrideSubtype.emit(subtype);
    }

    get reportData() {
        return this.reportDataInternal;
    }

    set reportData(value: any[]) {
        this.reportDataInternal = value;
        this.digitizeService.getLayerKeyField(this.definition.layer).pipe(
            map((keyField) => {
                let key = null;
                if (this.reportData && this.reportData.length === 1) {
                    key = this.reportData[0][keyField];
                }
                this.isolatedReport.setDigitizeKey(key);
            })
        );
    }


    get subtype() {
        return this.subTypeInternal;
    }

    get hasStreetView(): boolean {
        return this.subtype === 'detail' && !!this.definition.parcelAddressField;
    }
}
